import moment from 'moment';
import * as React from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Dropdown } from 'semantic-ui-react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import { Competition } from '../../Competitions/CompetitionsTypes';
import { addModifiers, getRankMessage, getStatusModifier } from '../../../shared/helpers';
import { FormattedMessageHelper, formatMessageHelper } from '../../../shared/helpers/internationalization';
import { formatNumber } from '../../../shared/helpers/formatNumber';
import { hasConsultantPermission, hasResearcherPermission } from '../../../reducers/AuthenticationReducer/AuthenticationSelector';
import {
  GENIUS_LEADERBOARD_PATH,
  POWER_POOL_COMPETITION_PATH,
  REFERRALS_COMPETITION_PATH,
  SPC_COMPETITION_PATH,
} from '../../../shared/constants';

import './CompetitionsDropdown.scss';
import cc from 'classcat';

interface CompetitionsDropdownProps {
  navClassName: string;
  competitions?: Competition[];
  rank: number | null;
  openGuidelinesModal: () => void;
  openMobileMenu: boolean;
  isChangingTabPosition: boolean;
}

interface ConsultantLeaderboardItem {
  key: string;
  to: string;
  labelId: string;
  rank?: number;
}

const CONSULTANT_LEADERBOARD_ITEMS: ReadonlyArray<ConsultantLeaderboardItem> = [
  { key: 'competitions-referrals-leaderboard', to: REFERRALS_COMPETITION_PATH, labelId: 'competitions.referrals' },
  { key: 'competitions-power-pool-leaderboard', to: POWER_POOL_COMPETITION_PATH, labelId: 'competitions.powerPool' },
  { key: 'competitions-spc-leaderboard', to: SPC_COMPETITION_PATH, labelId: 'header.spcLeaderboard' },
  { key: 'competitions-genius-leaderboard', to: GENIUS_LEADERBOARD_PATH, labelId: 'header.geniusLeaderboard' },
];

function isTouchDevice() {
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    // @ts-ignore msMaxTouchPoints in navigator
    navigator.msMaxTouchPoints > 0
  );
}

const isDropdownItem = (element: HTMLElement | null) =>
  (element && element.closest('.competitions__dropdown-link')) ||
  [...(element?.parentElement?.classList ?? [])].includes('competitions__dropdown-link');

const DATE_FORMAT = 'YYYY-M-DDTHH:mm:ssZ';

const useCompetitionTiming = (result: Competition) => {
  const now = moment();
  const competitionFinished = moment(result.endDate, DATE_FORMAT, true).isBefore(now);
  const competitionNotStarted = moment(result.startDate, DATE_FORMAT, true).isAfter(now);
  const signUpStartDate = moment(result.signUpStartDate, DATE_FORMAT, true);
  const signUpEndDate = moment(result.signUpEndDate, DATE_FORMAT, true);
  const isEnrolledPeriod = now.isBetween(signUpStartDate, signUpEndDate);

  return { competitionFinished, competitionNotStarted, isEnrolledPeriod };
};

interface CompetitionRightItemProps {
  result: Competition;
  openGuidelinesModal: () => void;
  isEnrolledPeriod: boolean;
  competitionFinished: boolean;
  competitionNotStarted: boolean;
}

const CompetitionRightItem = ({
  result,
  openGuidelinesModal,
  isEnrolledPeriod,
  competitionFinished,
  competitionNotStarted,
}: CompetitionRightItemProps) => {
  const rankMessage = getRankMessage(result.status);

  if (rankMessage === 'Rank') {
    if (competitionNotStarted) {
      return null;
    }
    const rank = result.leaderboard?.rank;
    if (rank === undefined) {
      return null;
    }
    return <FormattedMessageHelper id="header.rank.with.value" values={{ rank: formatNumber(rank, 'integer') }} />;
  }

  if (rankMessage === 'Restricted') {
    const rank = result.leaderboard?.rank;
    if (rank !== undefined && rank) {
      return <FormattedMessageHelper id="header.rank.with.value" values={{ rank: formatNumber(rank, 'integer') }} />;
    }
    if (!isEnrolledPeriod && competitionFinished) {
      return <FormattedMessageHelper id="header.concluded" />;
    }
    return <React.Fragment>{rankMessage}</React.Fragment>;
  }

  if (rankMessage === 'Enroll') {
    if (isEnrolledPeriod) {
      return (
        <button className="competitions__dropdown-right-column-underline" onClick={openGuidelinesModal}>
          <FormattedMessageHelper id="competitions.enroll" />
        </button>
      );
    }
    if (!isEnrolledPeriod && competitionFinished) {
      return <FormattedMessageHelper id="header.concluded" />;
    }
  }

  return null;
};

interface CompetitionListItemProps {
  result: Competition;
  isConsultantLayout: boolean;
  openGuidelinesModal: () => void;
}

const CompetitionListItem = ({ result, isConsultantLayout, openGuidelinesModal }: CompetitionListItemProps) => {
  const { competitionFinished, competitionNotStarted, isEnrolledPeriod } = useCompetitionTiming(result);
  const statusModifier = getStatusModifier(result.status, competitionFinished, isConsultantLayout);

  return (
    <Dropdown.Item className="competitions__dropdown competitions__dropdown--smooth" key={result.id}>
      <Link className="competitions__dropdown-link" to={`/competition/${result.id}`}>
        {!isConsultantLayout && <div className={addModifiers('competitions__dropdown-notification', [statusModifier])} />}
        <div className="competitions__dropdown-name">{result.name}</div>
        <div className={addModifiers('competitions__dropdown-right-column', [statusModifier])}>
          <CompetitionRightItem
            result={result}
            openGuidelinesModal={openGuidelinesModal}
            isEnrolledPeriod={isEnrolledPeriod}
            competitionFinished={competitionFinished}
            competitionNotStarted={competitionNotStarted}
          />
        </div>
      </Link>
    </Dropdown.Item>
  );
};

interface ActiveCompetitionsProps {
  competitions?: Competition[];
  isConsultantLayout: boolean;
  openGuidelinesModal: () => void;
}

const ActiveCompetitions = ({ competitions, isConsultantLayout, openGuidelinesModal }: ActiveCompetitionsProps) => {
  if (!competitions || competitions.length === 0) {
    return null;
  }

  return (
    <>
      <Dropdown.Divider className="competitions__dropdown-divider" key="active-competitions-divider" />
      <Dropdown.Item className="competitions__dropdown-section" key="active-competitions-heading">
        <div className="competitions__dropdown-section-title">
          <FormattedMessageHelper id="header.activeCompetitions" />
        </div>
      </Dropdown.Item>
      {competitions.map(result => (
        <CompetitionListItem
          key={result.id}
          result={result}
          isConsultantLayout={isConsultantLayout}
          openGuidelinesModal={openGuidelinesModal}
        />
      ))}
    </>
  );
};

const ViewAllCompetitions = () => (
  <Dropdown.Item className="competitions__dropdown" key="competitions-view-all">
    <Link className="competitions__dropdown-link" to="/competitions">
      <div className="competitions__dropdown-name competitions__dropdown-view-all">
        <FormattedMessageHelper id="header.viewAllCompetitions" />
      </div>
    </Link>
  </Dropdown.Item>
);

interface ConsultantOptionsProps {
  isResearcher: boolean;
  rank: number | null;
  competitions?: Competition[];
  openGuidelinesModal: () => void;
}

const ConsultantOptions = ({ isResearcher, rank, competitions, openGuidelinesModal }: ConsultantOptionsProps) => (
  <>
    <Dropdown.Item className="competitions__dropdown competitions__dropdown-section" key="consultant-exclusive-heading">
      <div className="competitions__dropdown-section-title">
        <FormattedMessageHelper id="header.consultantExclusive" />
      </div>
    </Dropdown.Item>
    {[
      {
        key: 'competitions-consultant-leaderboard',
        to: '/competition/consultant',
        labelId: isResearcher ? 'competitions.consultant.researcher' : 'competitions.consultant-leaderboard',
        rank,
      },
      ...CONSULTANT_LEADERBOARD_ITEMS,
    ].map(({ key, to, labelId, rank: itemRank }) => {
      return (
        <Dropdown.Item className="competitions__dropdown competitions__dropdown--smooth" key={key}>
          <Link className="competitions__dropdown-link" to={to}>
            <div className="competitions__dropdown-name">
              <FormattedMessageHelper id={labelId} />
            </div>
            {itemRank != null ? (
              <div className="competitions__dropdown-right-column competitions__dropdown-right-column--green">
                <FormattedMessageHelper id="header.rank.with.value" values={{ rank: formatNumber(itemRank, 'integer') }} />
              </div>
            ) : null}
          </Link>
        </Dropdown.Item>
      );
    })}
    <ActiveCompetitions competitions={competitions} isConsultantLayout openGuidelinesModal={openGuidelinesModal} />
    <Dropdown.Divider className="competitions__dropdown-divider" key="consultant-view-all-divider" />
    <ViewAllCompetitions />
  </>
);

interface UserOptionsProps {
  competitions?: Competition[];
  openGuidelinesModal: () => void;
}

const UserOptions = ({ competitions, openGuidelinesModal }: UserOptionsProps) => (
  <>
    {competitions?.map(competition => (
      <CompetitionListItem key={competition.id} result={competition} isConsultantLayout={false} openGuidelinesModal={openGuidelinesModal} />
    ))}
    {Boolean(competitions && competitions.length > 0) && (
      <Dropdown.Divider className="competitions__dropdown-divider" key="user-view-all-divider" />
    )}
    <ViewAllCompetitions />
  </>
);

interface CompetitionsOptionsProps {
  isConsultant: boolean;
  isResearcher: boolean;
  rank: number | null;
  competitions?: Competition[];
  openGuidelinesModal: () => void;
}

export const CompetitionsOptions = ({ isConsultant, isResearcher, rank, competitions, openGuidelinesModal }: CompetitionsOptionsProps) =>
  isConsultant || isResearcher ? (
    <ConsultantOptions isResearcher={isResearcher} rank={rank} competitions={competitions} openGuidelinesModal={openGuidelinesModal} />
  ) : (
    <UserOptions competitions={competitions} openGuidelinesModal={openGuidelinesModal} />
  );

export const CompetitionsDropdown = ({
  navClassName,
  competitions,
  rank,
  openGuidelinesModal,
  openMobileMenu,
  isChangingTabPosition,
}: CompetitionsDropdownProps) => {
  const intl = useIntl();
  const isConsultant = useSelector(hasConsultantPermission);
  const isResearcher = useSelector(hasResearcherPermission);
  const [openDropdown, setOpenDropdown] = React.useState(false);

  const isConsultantLayout = isConsultant || isResearcher;

  const size: number = competitions ? competitions.length : 0;
  const numCompetitions = isConsultantLayout ? size + CONSULTANT_LEADERBOARD_ITEMS.length + 1 : size;
  const label = React.useMemo(() => `${formatMessageHelper(intl, 'header.competitions')} (${numCompetitions})`, [intl, numCompetitions]);
  const headerImageClass = React.useMemo(
    () => addModifiers('header__img', isConsultant ? ['competitions-consultant'] : ['competitions']),
    [isConsultant]
  );

  // On mobile the submenu is not shown; fall back to a plain link to the competitions page.
  if (openMobileMenu) {
    return (
      <NavLink to="/competitions" activeClassName="active" className={`${navClassName}-link`}>
        <div className={`intro-dashboard-step-3 ${headerImageClass}`}>{label}</div>
      </NavLink>
    );
  }

  const handleDropdownClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    const target = e.target as HTMLElement;
    if (isDropdownItem(target)) {
      setOpenDropdown(false);
      return;
    }

    if (isTouchDevice()) {
      setOpenDropdown(prev => !prev);
    }
  };

  return (
    <div
      className={cc([
        navClassName === 'header__nav' ? 'header__nav-link' : '',
        addModifiers(`${navClassName}-competitions`, openDropdown ? ['opened'] : []),
      ])}
      onMouseOver={() => setOpenDropdown(true)}
      onMouseLeave={() => setOpenDropdown(false)}
    >
      <Dropdown
        className={`intro-dashboard-step-7 competitions__dropdown ${navClassName}-link ${headerImageClass}`}
        text={label}
        icon={false}
        onClick={handleDropdownClick}
        open={openDropdown}
      >
        <Dropdown.Menu className={addModifiers('competitions__dropdown-menu', isChangingTabPosition ? ['hide'] : [])} open={openDropdown}>
          <CompetitionsOptions
            isConsultant={isConsultant}
            isResearcher={isResearcher}
            rank={rank}
            competitions={competitions}
            openGuidelinesModal={openGuidelinesModal}
          />
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default CompetitionsDropdown;
